import React, { useState, useEffect } from "react";
import { stringify, parse } from "svgson";
import { UploadOutlined } from "@ant-design/icons";
import { Slider, InputNumber, Button, Upload } from "antd";
import { t } from "./i18n";

// Compact SVG -> PNG converter, mirroring the layout of the SVG-to-JPG tool
// (single file picker, slider, two number inputs, convert button) so it fits
// inside a short iframe without internal scrolling. Differs from SVG-to-JPG
// only in: (1) PNG output via canvas.toDataURL("image/png"), (2) transparent
// background (no fillRect), and (3) .png download filename.
function SVGtoPNGResizer() {
  const [file, setFile] = useState(null);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [aspectRatio, setAspectRatio] = useState(null);
  const [sliderDisabled, setSliderDisabled] = useState(true);
  const [inputWidth, setInputWidth] = useState(width);
  const [inputHeight, setInputHeight] = useState(height);
  const defaultWidth = 1500;

  const handleFileUpload = () => {
    if (!file) return;
    const reader = new FileReader();
    reader.readAsText(file);
    reader.onload = async () => {
      const svgText = reader.result;
      const svgJson = await parse(svgText);
      const canvas = document.createElement("canvas");

      const svgWidth = svgJson.attributes.viewBox.split(" ")[2];
      const svgHeight = svgJson.attributes.viewBox.split(" ")[3];
      const ratio = svgHeight / svgWidth;
      const newHeight = width * ratio;
      setHeight(newHeight);

      canvas.width = width;
      canvas.height = newHeight;

      const svgToSave = await stringify(svgJson);
      const img = new Image();
      img.src = "data:image/svg+xml;base64," + btoa(svgToSave);
      img.onload = function () {
        const ctx = canvas.getContext("2d");
        // PNG keeps transparency - skip background fill on purpose.
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataURL = canvas.toDataURL("image/png");

        const link = document.createElement("a");
        link.download = "image.png";
        link.href = dataURL;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      };
    };
  };

  const updateAspectRatio = (svgJson) => {
    const svgWidth = svgJson.attributes.viewBox.split(" ")[2];
    const svgHeight = svgJson.attributes.viewBox.split(" ")[3];
    const newAspectRatio = svgHeight / svgWidth;
    setAspectRatio(newAspectRatio);
    setSliderDisabled(false);
    setInputWidth(defaultWidth);
    setInputHeight(Math.round(defaultWidth * newAspectRatio));
  };

  useEffect(() => {
    if (file) {
      const reader = new FileReader();
      reader.readAsText(file);
      reader.onload = async () => {
        const svgText = reader.result;
        const svgJson = await parse(svgText);
        updateAspectRatio(svgJson);
      };
    } else {
      setWidth(defaultWidth);
      setHeight(aspectRatio ? Math.round(defaultWidth * aspectRatio) : defaultWidth);
      setInputWidth(defaultWidth);
      setInputHeight(aspectRatio ? Math.round(defaultWidth * aspectRatio) : defaultWidth);
      setSliderDisabled(true);
    }
  }, [file, aspectRatio]);

  useEffect(() => {
    if (aspectRatio) {
      setHeight(width * aspectRatio);
    }
  }, [aspectRatio, width]);

  const handleSliderChange = (value) => {
    setWidth(value);
    setHeight(value * aspectRatio);
    setInputWidth(value);
    setInputHeight(value * aspectRatio);
  };

  const handleInputNumberChange = (value, inputType) => {
    if (inputType === "width") {
      setInputWidth(value);
      setWidth(value);
      setHeight(value * aspectRatio);
      setInputHeight(value * aspectRatio);
    } else if (inputType === "height") {
      setInputHeight(value);
      setHeight(value);
      setWidth(value / aspectRatio);
      setInputWidth(value / aspectRatio);
    }
  };

  const props = {
    beforeUpload: (file) => {
      setFile(file);
      return false;
    },
    multiple: false,
    showUploadList: false,
    listType: "picture",
    accept: ".svg",
  };

  return (
    <div>
      <Upload {...props}>
        <Button type="primary" icon={<UploadOutlined />}>
          {t("Choose File")}
        </Button>
        {file && <span style={{ marginLeft: "10px" }}>{file.name}</span>}
      </Upload>
      <div style={{ maxWidth: "500px", margin: "0 auto" }}>
        <Slider
          min={16}
          max={5000}
          step={1}
          value={width}
          onChange={handleSliderChange}
          tipFormatter={(value) => {
            if (!aspectRatio) {
              return t("Choose a file");
            }
            return `${value} x ${Math.round(value * aspectRatio)} px`;
          }}
          disabled={sliderDisabled}
        />

        <InputNumber
          min={16}
          max={5000}
          step={1}
          value={inputWidth}
          onChange={(value) => handleInputNumberChange(value, "width")}
          formatter={(value) => `${Math.round(value)}`}
          onPressEnter={() => {
            setWidth(inputWidth);
            setHeight(inputWidth * aspectRatio);
          }}
          disabled={sliderDisabled}
        />
        <span>   x   </span>

        <InputNumber
          min={16}
          max={5000}
          step={1}
          value={inputHeight}
          onChange={(value) => handleInputNumberChange(value, "height")}
          formatter={(value) => `${Math.round(value)}`}
          onPressEnter={() => {
            setHeight(inputHeight);
            setWidth(inputHeight / aspectRatio);
          }}
          disabled={sliderDisabled}
        />
        <span>  px  </span>

        <div style={{ marginTop: "20px" }}>
          <Button disabled={sliderDisabled} onClick={handleFileUpload}>
            {t("Convert and Download")}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default SVGtoPNGResizer;
